// trades — client side of trades (Oct 28, 2026): a sub's visit to a job, like JAC Gutters detaching guards
// before the roof, resetting them after, a new install or a service call. The plan is
// docs/trade-scheduling-plan.md.
//
// Reads go straight to the tables (RLS): trade_items for anyone who can open the deal; trade_item_prices,
// vendor_prices and trade_item_changes only for trade_can_see_prices() (Alex, Robert, leadership, finance).
// Every write goes through the build-calendar edge function (trade_* actions), which puts the visit on the
// Other Trades Google calendar and texts the homeowner. This file is the typed wrapper and the small rules
// the Builds board and the deal page share.
//
// Oct 29 (Greg): soffit / fascia is a work of its own, and a trade can be with a sub typed on the sheet (an
// "other" sub): vendor is the typed name, vendor_id null, and its email / phone sit in details.
//
// Oct 29 (Greg): "the reps only care about the amount that we need to charge homeowner and where their commission
// starts." The sheet, the cards and the deal's Trades card show the minimum to charge (the 30% floor: the sub's
// cost / 0.7, tradeText floorOf / floorRate) and the dollars above it (upside, commission on a homeowner-billed
// trade) or under it (a shortfall, which joins the deal's non-collect bucket if Chad deducts it), never the sub's
// price; Chad's list on the Commission board keeps the sub's price. RLS still hands the price row (lines,
// agreed_total) to the same people as before: the floor is how it's shown, not who can read it.
//
// Oct 29 (Greg): "if a trade is added after the homeowner has signed, the addendum is made ... sign and schedule
// button are below ... once signed, the schedule button is available." On a signed roof job a homeowner-billed trade
// is an add-on the homeowner signs by text link (trade_item_prices.addendum_status, migration 20261029g): the sheet
// and the cards show its state with Sign (build-calendar trade_addendum_send) and Schedule (Move, once signed), and
// the Builds board lists a job waiting on the signature under Needs attention.

import { SUPABASE_URL, supabase } from "./supabase";
import { fetchEdge, readEdgeJson } from "./edgeError";
import { isAddendumOpen } from "../../supabase/functions/build-calendar/tradeText";
import type {
  AddendumStatus, BilledTo, MarginReview, PriceLine, PriceListItem, PriceStatus, TradeItem, TradeTiming, TradeWork,
} from "../../supabase/functions/build-calendar/tradeText";

export * from "../../supabase/functions/build-calendar/tradeText";

export type TradeStatus = "pending" | "scheduled" | "in_progress" | "complete" | "cancelled";

export interface Trade {
  id: string;
  deal_id: string;
  trade_type: TradeWork;
  scope_notes: string | null;
  vendor: string | null;
  vendor_id: string | null;
  scheduled_date: string | null;
  completed_date: string | null;
  status: TradeStatus;
  items: TradeItem[];
  /** vendor_email / vendor_phone: an other sub's (vendor_id null), typed on the sheet. */
  details: { gutter_size?: string; color?: string; other_label?: string; vendor_email?: string; vendor_phone?: string };
  pair_id: string | null;
  timing: TradeTiming;
  arrival_window: string | null;
  billed_to: BilledTo | null;
  assigned_by: string | null;
  calendar_event_id: string | null;
  calendar_html_link: string | null;
  calendar_status: string | null;
  calendar_source: "api" | "adopted" | null;
  needs_check: boolean;
  needs_check_reason: string | null;
  homeowner_told_date: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  created_at: string;
  updated_at: string;
  deal?: {
    homeowner_name: string | null;
    property_street: string | null;
    property_city: string | null;
    owner_id: string | null;
    build_scheduled_date: string | null;
    build_end_date: string | null;
    /** Once approved, the commission's lines are stored and its Books bill made, so a trade shortfall can't come off it (Oct 29). */
    commission_approved?: boolean | null;
    commission_paid?: boolean | null;
  } | null;
}

export interface TradePrice {
  trade_id: string;
  vendor_id: string | null;
  lines: PriceLine[];
  list_total: number | null;
  agreed_total: number | null;
  price_status: PriceStatus;
  rates_from: string | null;
  /** Greg, Oct 29: the price to the customer and Bardin's margin on it; short of the target it waits on Chad. */
  customer_total: number | null;
  margin_target_pct: number | null;
  margin_pct: number | null;
  margin_review: MarginReview;
  margin_review_reason: string | null;
  /** The shortfall Chad deducted, when he did: it joins the deal's non-collect bucket (the first $500 is forgiven). */
  margin_loss: number | null;
  /** The homeowner's line for this trade on the install agreement and the invoice (Oct 29; null on a row priced before). */
  customer_label?: string | null;
  /** The homeowner's add-on (Oct 29, migration 20261029g): needed on a signed roof job for a homeowner-billed price;
   *  until signed the job gets no day and isn't on the deal's money. Missing before the migration. */
  addendum_status?: AddendumStatus;
  addendum_amount?: number | null;
  addendum_label?: string | null;
  addendum_sent_at?: string | null;
  addendum_signed_at?: string | null;
  addendum_url?: string | null;
  margin_review_note: string | null;
  margin_reviewed_by: string | null;
  margin_reviewed_at: string | null;
  updated_at: string;
}

/** A flagged price with its trade and job, for Chad's list on the Commission board. */
export interface MarginReviewRow extends TradePrice {
  trade: Trade;
}

export interface TradeChange {
  id: string;
  trade_id: string;
  kind: "created" | "edited" | "moved" | "called_off" | "cancelled" | "done" | "roof_moved" | "still_good" | "margin_reviewed"
    | "addendum_sent" | "addendum_signed"
    | "invoiced" | "invoice_adjusted" | "paid" | "payment_voided" | "invoice_unlinked";
  from_date: string | null;
  to_date: string | null;
  reason: string | null;
  results: Record<string, unknown>;
  actor_id: string | null;
  created_at: string;
}

export interface TradeGuest {
  email: string;
  name: string;
  why: string;
}

export interface TradeVendorContext {
  id: string;
  name: string;
  primary_email: string | null;
  primary_phone: string | null;
  prices: PriceListItem[];
  prices_from: string | null;
  guests: TradeGuest[];
}

export interface TradeContext {
  ok: boolean;
  today: string;
  deal: {
    id: string;
    homeowner: string | null;
    street: string | null;
    address: string;
    phone_ok: boolean;
    opted_out: boolean;
    first_name: string;
    roof_start: string | null;
    roof_end: string | null;
    roof_expected: string | null;
    built: string | null;
    /** The deal's non-collect bucket (Greg, Oct 29: "let it blend into non-collect revenue"): non-collect revenue
     *  (an insurance deal whose deduction is on) + the shortfalls Chad deducted; the first $500 is forgiven, `over`
     *  comes off the commission. Missing from a function deployed before Oct 29. */
    bucket?: { non_collect: number; trade_margin_loss: number; allowance: number; over: number; is_retail: boolean } | null;
    /** The homeowner signed the install agreement: a homeowner-billed trade saved now on a roof job is an add-on the
     *  homeowner signs by text link before it gets a day. */
    agreement_signed?: boolean;
    /** A roof job (an insurance scope or a retail roof); a stand-alone trade job needs no add-on. Missing before Oct 29. */
    roof_job?: boolean;
    /** The deal's commission is approved or paid: a homeowner-billed trade's price and payer can't change. */
    commission_locked?: boolean;
  };
  vendors: TradeVendorContext[];
  /** The default guests for a sub typed on the sheet (standing guests and the RMs, no sub); the sheet adds the
   *  typed email. Missing from a function deployed before Oct 29: the sheet then takes the first vendor's, less its sub. */
  other_sub_guests?: TradeGuest[];
  prices_hidden: boolean;
  calendar_configured: boolean;
  /** The margin target (finance_settings.trade_margin_pct, 30). */
  margin_target_pct: number;
  /** The deal's "items not performing" line, and whether it names the gutter detach and reset insurance paid for. */
  scope_not_performing: string | null;
  scope_had_detach_reset: boolean;
  /** The parsed scope's gutter detach-and-reset lines and what the carrier pays for them (empty until the scope parser runs). */
  scope_detach_reset: { lines: Array<{ description: string; rcv: number | null }>; rcv_total: number | null };
}

export interface TradeCalendarResult {
  trade_id?: string;
  status: "created" | "updated" | "adopted" | "called_off" | "deleted" | "not_configured" | "error" | "skipped";
  event_id?: string;
  html_link?: string | null;
  error?: string;
  day_url?: string | null;
}

export interface TradeTextResult {
  status: "sent" | "queued" | "not_sent" | "not_needed" | "skipped" | "failed";
  reason?: string;
  error?: string;
  send_at?: string;
  texts?: string[];
  told_date?: string;
  /** How many of the texts went out (a second can fail after the first). */
  sent?: number;
  /** Visits that got no text and why (no approved wording, a detach too far from the roof, no words for the work). */
  skipped?: Array<{ trade_id?: string; reason: string; detail: string }>;
}

export interface TradeSaveResult {
  ok: boolean;
  trades: Array<{ id: string; work: TradeWork; date: string | null; status: string }>;
  price: {
    status: PriceStatus; agreed_total: number | null; list_total: number | null; lines: PriceLine[];
    customer_total: number | null; margin_pct: number | null; margin_target_pct: number; margin_review: MarginReview; margin_review_reason: string | null;
    /** The price on file, kept as it was (the sheet opened without it, or someone kept off prices saved). */
    kept?: boolean;
    /** Oct 30: "visit_done" when it was kept because the job's price sits on a visit already done. */
    kept_reason?: string;
    /** Oct 29: the minimum to charge (the floor on the sub's price; null while it waits on a quote) and the
     *  customer's price against it (+ above, - under; null without both). Missing from a function deployed before. */
    floor_total?: number | null;
    upside?: number | null;
  } | null;
  /** The trade saved but its price row didn't; why. */
  price_error?: string;
  /** The homeowner's add-on as the save left it (Oct 29); null with no price row, missing from an older function. */
  addendum?: TradeAddendumState | null;
  calendar: TradeCalendarResult[];
  homeowner_text: TradeTextResult;
}

/** A job's add-on (build-calendar trade_save's `addendum`). */
export interface TradeAddendumState {
  trade_id: string;
  status: AddendumStatus;
  sent_at: string | null;
  signed_at: string | null;
  amount: number | null;
  label: string;
}

/** Sign (build-calendar trade_addendum_send): the link texted, or why not. */
export interface AddendumSendResult {
  ok: boolean;
  status: "sent" | "not_sent";
  reason?: "no_phone" | "opted_out" | "muted" | "no_link" | "sent_recently" | "failed";
  error?: string;
  text: string | null;
  phone_last4?: string;
  sent_at?: string;
  trade_id?: string;
}

export const sendTradeAddendum = (tradeId: string) => tradeCall<AddendumSendResult>({ action: "trade_addendum_send", trade_id: tradeId });

/** What Sign says when the text didn't go. */
export function addendumNotSentText(r: Pick<AddendumSendResult, "reason" | "error">): string {
  switch (r.reason) {
    case "no_phone": return "Not sent: no phone on file for the homeowner.";
    case "opted_out": return "Not sent: the homeowner replied STOP.";
    case "muted": return "Not sent: texts to this homeowner are muted on the deal.";
    case "no_link": return "Not sent: the job has no street number or homeowner last name, so the link can't open.";
    case "sent_recently": return "Already sent within the hour. Try again later if they need it again.";
    case "failed": return `Couldn't send: ${r.error ?? "the text was refused"}.`;
    default: return "Not sent.";
  }
}

export interface TradeMoveResult {
  ok: boolean;
  trade: { id: string; old_date: string | null; new_date: string | null };
  calendar: TradeCalendarResult;
  homeowner_text: TradeTextResult;
}

/** A trade the roof's move flagged (schedule / reschedule results, v10). */
export interface FlaggedTrade {
  id: string;
  work: string;
  date: string | null;
  vendor: string | null;
  reason: string;
}

// ── who ──
// ⚠ Mirror of build-calendar's TRADE_ROLES (the scheduling tier plus asm); super users resolve to admin there.
const TRADE_ROLES = new Set(["roof_team_manager", "admin", "sales_director", "director_of_operations", "roof_director", "asm"]);

export function canTrade(role: string | null | undefined, isSuperUser: boolean): boolean {
  return isSuperUser || (!!role && TRADE_ROLES.has(role));
}

/** Ramiro (project_manager) can also mark a visit done. */
export function canMarkTradeDone(role: string | null | undefined, isSuperUser: boolean): boolean {
  return canTrade(role, isSuperUser) || role === "project_manager";
}

// ⚠ Mirror of build-calendar's MARGIN_REVIEW_ROLES: Chad (sales_director) and the admin tier.
export function canReviewMargins(role: string | null | undefined, isSuperUser: boolean): boolean {
  return isSuperUser || role === "admin" || role === "sales_director";
}

// ── the edge function ──

// Not buildCalendar() from ./buildCalendar: its build wording would replace the trade server's own (its
// "same_date" says "That's already the build date.").
export async function tradeCall<T>(body: Record<string, unknown>): Promise<T> {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess?.session?.access_token;
  if (!token) throw new Error("You've been signed out. Sign back in and try again.");
  const res = await fetchEdge(`${SUPABASE_URL}/functions/v1/build-calendar`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  const out = await readEdgeJson<Record<string, unknown>>(res);
  if (!res.ok || !out?.ok) {
    const code = (out?.error as string) || "";
    const detail = (out?.detail as string) || "";
    if (code === "unauthorized") throw new Error("You've been signed out. Sign back in and try again.");
    if (code === "forbidden") throw new Error(detail || "Your account can't do that.");
    throw new Error(detail || code || `Request failed (HTTP ${res.status}).`);
  }
  return out as unknown as T;
}

export const fetchTradeContext = (dealId: string) => tradeCall<TradeContext>({ action: "trade_context", deal_id: dealId });

// ── reads ──

// A left embed, not !inner: Ramiro (project_manager) reads trades to mark one done but has no deals policy,
// so his rows come back with deal: null (review, Oct 29). Every reader guards t.deal.
const TRADE_SELECT =
  "*, deal:deals(homeowner_name,property_street,property_city,owner_id,build_scheduled_date,build_end_date,commission_approved,commission_paid)";

function asTrades(data: unknown): Trade[] {
  return ((data ?? []) as Trade[]).map((t) => ({ ...t, items: Array.isArray(t.items) ? t.items : [], details: t.details ?? {} }));
}

/** One visit, for the Move flow the sheet's Schedule opens (Oct 29). */
export async function fetchTradeById(id: string): Promise<Trade | null> {
  const { data, error } = await supabase.from("trade_items").select(TRADE_SELECT).eq("id", id).maybeSingle();
  if (error) throw error;
  return data ? asTrades([data])[0] : null;
}

export async function fetchDealTrades(dealId: string): Promise<Trade[]> {
  const { data, error } = await supabase.from("trade_items").select(TRADE_SELECT).eq("deal_id", dealId)
    .order("scheduled_date", { ascending: true, nullsFirst: true }).order("created_at", { ascending: true });
  if (error) throw error;
  return asTrades(data);
}

/** The queue: every open visit, and the ones finished or cancelled in the last two weeks. */
export async function fetchTradeQueue(today: string): Promise<Trade[]> {
  const since = new Date(Date.parse(`${today}T12:00:00Z`) - 14 * 86400000).toISOString();
  const [open, recent] = await Promise.all([
    supabase.from("trade_items").select(TRADE_SELECT).in("status", ["pending", "scheduled", "in_progress"]),
    supabase.from("trade_items").select(TRADE_SELECT).in("status", ["complete", "cancelled"]).gte("updated_at", since),
  ]);
  if (open.error) throw open.error;
  if (recent.error) throw recent.error;
  return [...asTrades(open.data), ...asTrades(recent.data)];
}

/** Visits on the days a calendar view shows. */
export async function fetchTradesBetween(from: string, to: string): Promise<Trade[]> {
  const { data, error } = await supabase.from("trade_items").select(TRADE_SELECT)
    .gte("scheduled_date", from).lt("scheduled_date", to).in("status", ["scheduled", "in_progress", "complete"]);
  if (error) throw error;
  return asTrades(data);
}

/** Prices for these trades, by trade id. Empty for someone kept off prices (RLS answers no rows). */
export async function fetchTradePrices(tradeIds: string[]): Promise<Record<string, TradePrice>> {
  const out: Record<string, TradePrice> = {};
  for (let i = 0; i < tradeIds.length; i += 150) {
    const { data, error } = await supabase.from("trade_item_prices").select("*").in("trade_id", tradeIds.slice(i, i + 150));
    if (error) throw error;
    for (const p of (data ?? []) as TradePrice[]) {
      out[p.trade_id] = {
        ...p, list_total: numOrNull(p.list_total), agreed_total: numOrNull(p.agreed_total), customer_total: numOrNull(p.customer_total),
        margin_pct: numOrNull(p.margin_pct), margin_target_pct: numOrNull(p.margin_target_pct), margin_loss: numOrNull(p.margin_loss),
        addendum_amount: numOrNull(p.addendum_amount),
      };
    }
  }
  return out;
}

/** Chad's list: every price waiting on him, with its trade and job. */
export async function fetchMarginReviewQueue(): Promise<MarginReviewRow[]> {
  const { data, error } = await supabase.from("trade_item_prices")
    .select(`*, trade:trade_items!inner(${TRADE_SELECT})`)
    .eq("margin_review", "needs_review")
    .order("updated_at", { ascending: true });
  if (error) throw error;
  // A finished trade still waits on Chad: the work being done doesn't settle its price (delta review, Oct 29).
  return ((data ?? []) as Array<TradePrice & { trade: Trade }>)
    .filter((r) => r.trade && r.trade.status !== "cancelled")
    .map((r) => ({
      ...r, list_total: numOrNull(r.list_total), agreed_total: numOrNull(r.agreed_total), customer_total: numOrNull(r.customer_total),
      margin_pct: numOrNull(r.margin_pct), margin_target_pct: numOrNull(r.margin_target_pct), margin_loss: numOrNull(r.margin_loss), trade: asTrades([r.trade])[0],
    }));
}

const numOrNull = (v: unknown): number | null => (v === null || v === undefined ? null : Number(v));

export async function fetchTradeChanges(tradeIds: string[]): Promise<TradeChange[]> {
  if (!tradeIds.length) return [];
  const { data, error } = await supabase.from("trade_item_changes").select("*").in("trade_id", tradeIds).order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as TradeChange[];
}

// ── rules the board and the deal page share ──

/** A pair's price lives on its first visit; either visit shows it. */
export function priceFor(t: Trade, all: Trade[], prices: Record<string, TradePrice>): { price: TradePrice; own: boolean } | null {
  if (prices[t.id]) return { price: prices[t.id], own: true };
  if (!t.pair_id) return null;
  const partner = all.find((o) => o.pair_id === t.pair_id && o.id !== t.id && prices[o.id]);
  return partner ? { price: prices[partner.id], own: false } : null;
}

export function isOpenTrade(t: Trade): boolean {
  return t.status === "pending" || t.status === "scheduled" || t.status === "in_progress";
}

/** The homeowner hasn't signed this job's add-on yet (Oct 29): the job gets no day and isn't billed until they do. */
export const ADDENDUM_WAITING = "Waiting on the homeowner's add-on signature";

/** Why a visit needs someone, or null. `jobPrice` is the job's price row whichever visit carries it (a pair's add-on
 *  holds both visits); `price` only this visit's own. */
export function tradeAttention(t: Trade, today: string, price: TradePrice | null, jobPrice: TradePrice | null = price): string | null {
  if (!isOpenTrade(t)) return null;
  if (t.needs_check) return t.needs_check_reason ?? "The roof moved. Check this day with the sub.";
  if (isAddendumOpen(jobPrice?.addendum_status)) return ADDENDUM_WAITING;
  if (t.status === "pending") return "Get a day from the sub.";
  if (t.scheduled_date && t.scheduled_date < today) return "The day passed. Mark it done or move it.";
  if (price?.price_status === "quote_pending") return "Waiting on a price from the sub";
  return null;
}

export const MARGIN_REVIEW_LABELS: Record<MarginReview, string> = {
  none: "", needs_review: "Chad reviewing the price", allowed: "Allowed by Chad, no recourse", deducted: "Shortfall sent to the non-collect bucket",
};

export const BILLED_TO_LABELS: Record<"insurance" | "homeowner" | "bardin", string> = {
  insurance: "Insurance",
  homeowner: "Homeowner",
  bardin: "Bardin at cost",
};

export const PRICE_STATUS_LABELS: Record<PriceStatus, string> = {
  list: "List price",
  agreed: "Agreed price",
  quote_pending: "Waiting on a price",
};

/** "$1,687.50" */
export function fmtMoney2(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "";
  return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** "Thu, Nov 5" for a date-only string, without a timezone slip. */
export function fmtTradeDay(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

// ── the JAC sales order in Books (Oct 29, 2026; migration 20261029h) ──
// One order per job (the price row), keyed tradeJobKey(t); what Books says about JAC's invoice comes back into
// trade_books_orders, which only the price viewers read. The chip's words are in ./tradeOrderText (pure, tested).
export {
  TRADE_ORDER_COLS, TRADE_ORDER_TONE_CLASSES, orderChipPlace, tradeJobKey, tradeOrderChip,
  type TradeOrder, type TradeOrderChipView, type TradeOrderStatus, type TradeOrderTone,
} from "./tradeOrderText";
import { TRADE_ORDER_COLS, type TradeOrder } from "./tradeOrderText";

/** The jobs' orders by job key (no money columns). Empty for anyone kept off prices: RLS answers no rows. */
export async function fetchTradeOrders(jobKeys: string[]): Promise<Record<string, TradeOrder>> {
  const keys = [...new Set(jobKeys.filter(Boolean))];
  const out: Record<string, TradeOrder> = {};
  for (let i = 0; i < keys.length; i += 150) {
    const { data, error } = await supabase.from("trade_books_orders").select(TRADE_ORDER_COLS).in("job_key", keys.slice(i, i + 150));
    if (error) throw error;
    for (const o of (data ?? []) as unknown as TradeOrder[]) {
      out[o.job_key] = { ...o, match_differences: Array.isArray(o.match_differences) ? o.match_differences : null };
    }
  }
  return out;
}
